import React, { useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { headerCellStyle, bodyCellStyle } from "./components/SelectableEnrollmentTable";

interface BulkReminderSlipModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
}

/**
 * Reminder Slip (bulk) — mirrors the legacy modal: a Date (as-of cut-off), a
 * read-only Selected Students list, and a Reminder Slip print button. Print
 * generates each selected student's outstanding dues as of the date (reusing the
 * per-enrollment reminder-slip HTML endpoint), concatenated one-per-page, and
 * opens the browser print dialog.
 */
const BulkReminderSlipModal: React.FC<BulkReminderSlipModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
}) => {
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");

  // One row per enrollment under the selected students: { enrollmentId, name, code }.
  const rows = useMemo(() => {
    const nameByEnquiryId = new Map<number, string>();
    for (const s of studentList) nameByEnquiryId.set(s.enquiryId, s.name);
    const out: Array<{ enrollmentId: number; name: string; code: string }> = [];
    for (const eqId of selectedEnquiryIds) {
      for (const enr of enquiryEnrollmentsMap[eqId] || []) {
        if (enr?.enrollmentId) {
          out.push({
            enrollmentId: enr.enrollmentId,
            name: nameByEnquiryId.get(eqId) || "",
            code: enr.studentCode || "",
          });
        }
      }
    }
    return out;
  }, [selectedEnquiryIds, enquiryEnrollmentsMap, studentList]);

  const handlePrint = async () => {
    setError("");
    if (rows.length === 0) {
      setError("The selected students have no enrollments.");
      return;
    }
    const w = window.open("", "_blank");
    if (w) {
      w.document.write("<p style='font-family:sans-serif;padding:16px'>Generating reminder slip…</p>");
    }
    setPrinting(true);
    try {
      const responses = await Promise.all(
        rows.map((r) => EnrollmentService.getReminderSlip(r.enrollmentId, date || undefined)),
      );
      const htmls = responses.map((r) => r?.data?.html || "").filter((h) => h);
      if (htmls.length === 0) {
        setError("No reminder slip could be generated for the selected students.");
        if (w) w.close();
        return;
      }
      const combined = htmls.join('<p style="page-break-after:always;"></p>');
      if (w) {
        w.document.open();
        w.document.write(combined);
        w.document.close();
        w.focus();
        w.print();
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to generate reminder slip");
      if (w) w.close();
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Reminder Slip</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <Form.Group className="mb-3" style={{ flex: "0 0 200px" }}>
            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Date</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Form.Group>

          <div style={{ flex: "1 1 320px" }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--text-strong)",
                marginBottom: 6,
              }}
            >
              Selected Students
            </div>
            <div
              style={{
                maxHeight: 240,
                overflow: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...headerCellStyle, width: 60 }}>Sl no.</th>
                    <th style={headerCellStyle}>Student Name/Code</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.enrollmentId}>
                      <td style={bodyCellStyle}>{i + 1}</td>
                      <td style={bodyCellStyle}>
                        {r.name}
                        {r.code ? ` ( ${r.code} )` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={printing}>
          Close
        </Button>
        <Button variant="primary" onClick={handlePrint} disabled={printing || rows.length === 0}>
          {printing ? (
            <>
              <i className="fa fa-spinner fa-spin" /> Generating…
            </>
          ) : (
            <>Reminder Slip</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkReminderSlipModal;
